import { Action } from "redux-saga"

import { iriFromIModelOrIRI } from "@api/entityTypeEndpointDefinitions"
import { ITeamUpload } from "@api/schema"
import { AttachmentDefinitionType, IAttachmentDefinition, IDocumentAttachmentDefinition, IDocumentProposalAttachment, IProjectPlanAttachmentDefinition, IProjectPlanProposalAttachment, IProposal, IProposalAttachment } from "@api/schema/challenges"
import { IFormikActions } from "@basics/form"
import { EntityType } from "@redux/common/reduxTypes"

import { CreateableProposalAttachmentEntityType, IAttachmentDtoWithFile, IAttachmentTeamUploadDto } from "./schema-definitions"

/**
 * Definition of the interaction with the host application backend API for performing proposal attachment processes
 * to be implemented by the client.
 */
export interface IProposalAttachmentEntityApi {
  /**
   * Creating a proposal attachment from a team upload
   *
   * @param entityType
   * @param attachmentTeamUploadInput dto for creating a proposal attachment from a team upload
   * @returns a standard operation result output
   */
  createProposalAttachmentFromTeamUpload: (entityType: CreateableProposalAttachmentEntityType, attachmentTeamUploadInput: IAttachmentTeamUploadDto) => Promise<IProposalAttachment>
  /**
   * Creating a document proposal attachment or project plan proposal attachment from a file.
   *
   * @param entityType
   * @param attachmentFileInput dto for creating a proposal attachment from a file
   * @returns a standard operation result output
   */
  createProposalAttachmentFromFile: (entityType: EntityType.DocumentProposalAttachment | EntityType.ProjectPlanProposalAttachment, attachmentFileInput: IAttachmentDtoWithFile) => Promise<IDocumentProposalAttachment | IProjectPlanProposalAttachment>
}

/**
 * This enum defines the usecase for reporting an entity.
 */
export enum ProposalAttachmentUsecases {
  ProposalAttachmentFromTeamUpload = "_proposal_attachment_from_team_upload",
  DocumentOrProjectPlanProposalAttachmentFromFile = "_proposal_attachment_from_file",
}

export interface IGeneralProposalAttachmentAction<
  Usecases extends ProposalAttachmentUsecases,
  GenericAttachmentDefinitionType extends AttachmentDefinitionType = AttachmentDefinitionType
> extends Action<Usecases> {
  /** formik actions to interact with the triggering form */
  callbackActions: IFormikActions
  /** determine for which attachment definitin will the team upload be added */
  definition: IAttachmentDefinition<GenericAttachmentDefinitionType>
  /** determine for which proposal will the team upload be added */
  proposal: IProposal
}

/**
 * Action type for creating a proposal attachment from a team upload.
 */
export interface IProposalAttachmentFromTeamUploadAction extends IGeneralProposalAttachmentAction<ProposalAttachmentUsecases.ProposalAttachmentFromTeamUpload> {
  /** team upload to be used as an attachment */
  teamUpload: ITeamUpload
}

export interface IProposalAttachmentFromFileAction extends IGeneralProposalAttachmentAction<
  ProposalAttachmentUsecases.DocumentOrProjectPlanProposalAttachmentFromFile,
  AttachmentDefinitionType.Document | AttachmentDefinitionType.ProjectPlan
> {
  entityType: EntityType.DocumentProposalAttachment | EntityType.ProjectPlanProposalAttachment
  file: File
}

/**
 * Creates an Action to create a proposal attachment from a team upload.
 */
export const createProposalAttachmentFromTeamUploadAction = (teamUpload: ITeamUpload, definition: IAttachmentDefinition, proposal: IProposal, actions: IFormikActions): IProposalAttachmentFromTeamUploadAction =>
({
  teamUpload,
  definition,
  proposal,
  callbackActions: actions,
  type: ProposalAttachmentUsecases.ProposalAttachmentFromTeamUpload
})

/**
 * Creates an Action to create a document/project plan proposal attachment from a file.
 */
export const createProposalAttachmentFromFileAction = (entityType: EntityType.DocumentProposalAttachment | EntityType.ProjectPlanProposalAttachment, file: File, definition: IDocumentAttachmentDefinition | IProjectPlanAttachmentDefinition, proposal: IProposal, actions: IFormikActions): IProposalAttachmentFromFileAction =>
({
  entityType,
  file,
  definition,
  proposal,
  callbackActions: actions,
  type: ProposalAttachmentUsecases.DocumentOrProjectPlanProposalAttachmentFromFile
})

/**
 * Creates a unique usecaseKey for creating a proposal attachment from a team upload.
 */
export const usecaseKeyForProposalAttachmentFromTeamUpload = (teamUpload: ITeamUpload): string =>
  ProposalAttachmentUsecases.ProposalAttachmentFromTeamUpload + iriFromIModelOrIRI(teamUpload)

/**
 * Creates a unique usecaseKey for creating a document/project plan proposal attachment from a file.
 */
export const usecaseKeyForProposalAttachmentFromFile = (definition: IAttachmentDefinition, proposal: IProposal,): string =>
  ProposalAttachmentUsecases.DocumentOrProjectPlanProposalAttachmentFromFile + iriFromIModelOrIRI(definition) + iriFromIModelOrIRI(proposal)
